import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Phone, ArrowRight, Loader2, ChevronLeft } from 'lucide-react';
import { useUserStore } from '../store/userStore';
import { syncWebPushSubscription } from '../services/pushNotifications';
import { loyaltyApi } from '../services/loyaltyApi';
import { getTelegramInitData } from '../telegram/telegramWebApp';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5050';

function messageFromApiPayload(data: unknown, fallback: string): string {
  if (!data || typeof data !== 'object') return fallback;
  const m = (data as { message?: unknown }).message;
  if (typeof m === 'string' && m.trim()) return m.trim();
  if (Array.isArray(m) && typeof m[0] === 'string') return m[0];
  return fallback;
}

function smsOfferFromPayload(data: unknown): boolean {
  return !!(
    data &&
    typeof data === 'object' &&
    (data as { smsOffer?: boolean }).smsOffer === true
  );
}

/** Нормализует к 7XXXXXXXXXX (макс. 11 цифр). */
function normalizePhoneDigits(value: string): string {
  let digits = value.replace(/\D/g, '');
  if (!digits) return '';
  if (digits.startsWith('8')) digits = '7' + digits.slice(1);
  else if (!digits.startsWith('7')) digits = '7' + digits;
  return digits.slice(0, 11);
}

/** Маска без «висячих» разделителей — иначе backspace застревает на `-` / `)`. */
function formatPhone(digitsRaw: string) {
  const digits = normalizePhoneDigits(digitsRaw);
  if (!digits) return '';

  const rest = digits.slice(1); // после кода страны
  let result = '+7';
  if (rest.length === 0) return result;

  result += ' (' + rest.slice(0, 3);
  if (rest.length <= 3) return result;

  result += ') ' + rest.slice(3, 6);
  if (rest.length <= 6) return result;

  result += '-' + rest.slice(6, 8);
  if (rest.length <= 8) return result;

  result += '-' + rest.slice(8, 10);
  return result;
}

function formatDialDisplay(num: string): string {
  const d = num.replace(/\D/g, '');
  if (d.length === 11 && d.startsWith('7')) {
    return `+7 (${d.slice(1, 4)}) ${d.slice(4, 7)}-${d.slice(7, 9)}-${d.slice(9)}`;
  }
  return num;
}

type PendingAuth = { user: { id: string; firstName: string; lastName?: string | null }; token: string };

export default function OnboardingScreen() {
  const [step, setStep] = useState<'phone' | 'reverse_flash' | 'code' | 'intro' | 'notifications'>('phone');
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState(['', '', '', '']);
  const [firstNameInput, setFirstNameInput] = useState('');
  const [lastNameInput, setLastNameInput] = useState('');
  const [pendingAuth, setPendingAuth] = useState<PendingAuth | null>(null);
  const [otpChannel, setOtpChannel] = useState<'flash_call' | 'sms' | null>(null);
  const [reverseSessionId, setReverseSessionId] = useState<string | null>(null);
  const [reverseDialNumber, setReverseDialNumber] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [smsOfferVisible, setSmsOfferVisible] = useState(false);
  const reverseAutoVerifyStarted = useRef(false);
  const codeRefs = [
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
  ];
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const login = useUserStore((state) => state.login);
  const afterLoginPath = (() => {
    const next = searchParams.get('next');
    if (next && next.startsWith('/') && !next.startsWith('//')) return next;
    return '/app/home';
  })();

  function handlePhoneChange(e: React.ChangeEvent<HTMLInputElement>) {
    const nextValue = e.target.value;
    const prevDigits = phone.replace(/\D/g, '');
    let nextDigits = nextValue.replace(/\D/g, '');

    // Стирают разделитель маски (длина строки ↓, цифр столько же) → удаляем последнюю цифру
    if (nextValue.length < phone.length && nextDigits.length >= prevDigits.length && prevDigits.length > 1) {
      nextDigits = prevDigits.slice(0, -1);
    }

    setPhone(formatPhone(nextDigits));
    setError('');
    setSmsOfferVisible(false);
  }

  function handlePhoneKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key !== 'Backspace') return;
    const input = e.currentTarget;
    const pos = input.selectionStart ?? phone.length;
    const end = input.selectionEnd ?? pos;
    // Курсор сразу после разделителя и нет выделения → стираем цифру перед ним
    if (pos === end && pos > 0 && pos === phone.length && /[\s\-)]$/.test(phone)) {
      e.preventDefault();
      const digits = phone.replace(/\D/g, '');
      if (digits.length > 1) setPhone(formatPhone(digits.slice(0, -1)));
    }
  }

  async function handleSendCode() {
    const digits = phone.replace(/\D/g, '');
    if (digits.length < 11) {
      setError('Введите полный номер телефона');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const res = await fetch(`${API_URL}/auth/send-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: '+' + digits }),
      });

      const payload = await res.json().catch(() => ({}));
      if (!res.ok) {
        setSmsOfferVisible(smsOfferFromPayload(payload));
        setError(messageFromApiPayload(payload, 'Не удалось отправить код'));
        return;
      }
      setSmsOfferVisible(false);
      if (
        payload.channel === 'flash_call_reverse' &&
        payload.sessionId &&
        payload.dialNumber
      ) {
        reverseAutoVerifyStarted.current = false;
        setReverseSessionId(payload.sessionId);
        setReverseDialNumber(payload.dialNumber);
        setStep('reverse_flash');
        return;
      }
      setOtpChannel(payload.channel === 'flash_call' ? 'flash_call' : 'sms');
      setStep('code');
      setTimeout(() => codeRefs[0].current?.focus(), 100);
    } catch {
      setSmsOfferVisible(false);
      setError('Не удалось отправить код');
    } finally {
      setLoading(false);
    }
  }

  async function handleSendCodeViaSms() {
    const digits = phone.replace(/\D/g, '');
    if (digits.length < 11) {
      setError('Введите полный номер телефона');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/auth/send-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: '+' + digits, channel: 'sms' }),
      });
      const payload = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(messageFromApiPayload(payload, 'Не удалось отправить SMS'));
        return;
      }
      setSmsOfferVisible(false);
      setOtpChannel('sms');
      setStep('code');
      setTimeout(() => codeRefs[0].current?.focus(), 100);
    } catch {
      setError('Не удалось отправить SMS');
    } finally {
      setLoading(false);
    }
  }

  function handleCodeChange(index: number, value: string) {
    const digit = value.replace(/\D/g, '').slice(-1);
    const next = [...code];
    next[index] = digit;
    setCode(next);
    setError('');
    if (digit && index < 3) {
      codeRefs[index + 1].current?.focus();
    }
    if (next.every((d) => d !== '')) {
      handleVerifyCode(next.join(''));
    }
  }

  function handleCodeKeyDown(index: number, e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      codeRefs[index - 1].current?.focus();
    }
  }

  const verifyOtpRef = useRef<(codeValue?: string) => Promise<void>>(async () => {});

  async function handleVerifyCode(codeValue?: string) {
    const fullCode = codeValue ?? code.join('');
    if (fullCode.length < 4) {
      setError('Введите 4-значный код');
      return;
    }

    const digits = phone.replace(/\D/g, '');
    setLoading(true);
    setError('');

    try {
      const initData = getTelegramInitData();
      const tgUser = window.Telegram?.WebApp?.initDataUnsafe?.user;
      const data = initData
        ? await loyaltyApi.telegramPhoneLogin({
            initData,
            phone: '+' + digits,
            code: fullCode,
            ...(tgUser?.first_name ? { firstName: tgUser.first_name } : {}),
            ...(tgUser?.last_name ? { lastName: tgUser.last_name } : {}),
          })
        : await (async () => {
            const res = await fetch(`${API_URL}/users/phone-login`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ phone: '+' + digits, code: fullCode }),
            });
            const payload = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(messageFromApiPayload(payload, 'Ошибка'));
            return payload;
          })();

      if (data.isNewUser === true) {
        setPendingAuth({ user: data.user, token: data.token });
        setStep('intro');
        setFirstNameInput(
          data.user?.firstName && data.user.firstName !== 'Гость'
            ? data.user.firstName
            : tgUser?.first_name || '',
        );
        setLastNameInput(data.user?.lastName || tgUser?.last_name || '');
      } else {
        login({
          phone: data.user?.phone || '+' + digits,
          userId: data.user.id,
          token: data.token,
          firstName: data.user.firstName || 'Гость',
          lastName: data.user.lastName ?? '',
        });
        navigate(afterLoginPath);
      }
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : '';
      if (msg === 'Неверный код') setError('Неверный код');
      else if (msg && msg !== 'Ошибка') setError(msg);
      else setError('Не удалось войти. Попробуйте снова.');
      if (reverseSessionId) {
        setReverseSessionId(null);
        setReverseDialNumber('');
        setStep('code');
        setOtpChannel('sms');
        reverseAutoVerifyStarted.current = false;
      }
      setCode(['', '', '', '']);
      setTimeout(() => codeRefs[0].current?.focus(), 100);
    } finally {
      setLoading(false);
    }
  }

  verifyOtpRef.current = handleVerifyCode;

  useEffect(() => {
    if (step !== 'reverse_flash' || !reverseSessionId) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const r = await fetch(
          `${API_URL}/auth/reverse-flash-status?sessionId=${encodeURIComponent(reverseSessionId)}`,
        );
        if (!r.ok || cancelled) return;
        const j = (await r.json()) as { status?: string; unlockCode?: string };
        if (j.status === 'ready' && j.unlockCode) {
          if (reverseAutoVerifyStarted.current || cancelled) return;
          reverseAutoVerifyStarted.current = true;
          await verifyOtpRef.current(j.unlockCode);
          return;
        }
        if (j.status === 'expired' || j.status === 'unknown') {
          if (!cancelled) {
            setError('Время ожидания истекло. Запросите код снова.');
            setStep('phone');
            setReverseSessionId(null);
            setReverseDialNumber('');
          }
        }
      } catch {
        /* ignore */
      }
    };
    void tick();
    const id = setInterval(() => void tick(), 2000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [step, reverseSessionId]);

  function completeLogin(u: { firstName?: string | null; lastName?: string | null }) {
    if (!pendingAuth) return;
    const d = phone.replace(/\D/g, '');
    const first = (u.firstName ?? pendingAuth.user.firstName ?? 'Гость').trim() || 'Гость';
    login({
      phone: '+' + d,
      userId: pendingAuth.user.id,
      token: pendingAuth.token,
      firstName: first,
      lastName: (u.lastName ?? '').trim(),
    });
    navigate(afterLoginPath);
  }

  async function handleIntroContinue() {
    const first = firstNameInput.trim();
    const last = lastNameInput.trim();
    const combined = [first, last].filter(Boolean).join(' ');
    if (!combined) {
      handleIntroSkip();
      return;
    }
    if (!pendingAuth) return;
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/users/${pendingAuth.user.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${pendingAuth.token}`,
        },
        body: JSON.stringify({
          ...(first ? { firstName: first } : {}),
          ...(last ? { lastName: last } : {}),
        }),
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setPendingAuth({
        user: {
          id: pendingAuth.user.id,
          firstName: data.user?.firstName ?? first,
          lastName: data.user?.lastName ?? null,
        },
        token: pendingAuth.token,
      });
      setStep('notifications');
    } catch {
      setError('Не удалось сохранить');
    } finally {
      setLoading(false);
    }
  }

  function handleIntroSkip() {
    if (!pendingAuth) return;
    setStep('notifications');
  }

  async function handleNotificationsYes() {
    if (!pendingAuth) return;
    setLoading(true);
    try {
      await syncWebPushSubscription(pendingAuth.token);
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
    const u = pendingAuth.user;
    completeLogin({
      firstName: u.firstName || 'Гость',
      lastName: u.lastName ?? '',
    });
  }

  function handleNotificationsSkip() {
    if (!pendingAuth) return;
    const u = pendingAuth.user;
    completeLogin({
      firstName: u.firstName || 'Гость',
      lastName: u.lastName ?? '',
    });
  }

  useEffect(() => {
    document.documentElement.classList.add('landing-page');
    return () => document.documentElement.classList.remove('landing-page');
  }, []);

  const cardStyle: React.CSSProperties = {
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.1)',
  };
  const labelMuted: React.CSSProperties = {
    color: 'rgba(255,255,255,0.45)',
    fontFamily: 'Manrope, sans-serif',
  };
  const primaryBtn: React.CSSProperties = {
    background: '#fff',
    color: '#0a0a0a',
    fontFamily: 'Manrope, sans-serif',
  };
  const inputStyle = (hasError?: boolean): React.CSSProperties => ({
    background: 'rgba(255,255,255,0.04)',
    border: hasError ? '1px solid rgba(248,113,113,0.55)' : '1px solid rgba(255,255,255,0.12)',
    caretColor: '#fff',
    fontFamily: 'Manrope, sans-serif',
  });

  return (
    <div
      className="min-h-screen flex flex-col relative overflow-hidden text-white"
      style={{ background: '#0a0a0a' }}
    >
      <div className="flex-1 flex flex-col items-center justify-center px-5 sm:px-6 relative z-10 py-10">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
          className="text-center mb-10"
        >
          <h1
            className="text-4xl sm:text-5xl font-bold uppercase mb-3"
            style={{ fontFamily: 'Unbounded, sans-serif', letterSpacing: '-0.04em' }}
          >
            Теория
          </h1>
          <p
            className="text-[11px] tracking-[0.28em] uppercase"
            style={{ color: 'rgba(255,255,255,0.45)', fontFamily: 'Manrope, sans-serif' }}
          >
            Кухня &amp; бар
          </p>
        </motion.div>

        <div className="w-full max-w-sm">
          <AnimatePresence mode="wait">
            {step === 'reverse_flash' ? (
              <motion.div
                key="reverse_flash"
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -16 }}
                transition={{ duration: 0.35 }}
              >
                <div className="rounded-3xl p-6" style={cardStyle}>
                  <button
                    type="button"
                    onClick={() => {
                      setStep('phone');
                      setReverseSessionId(null);
                      setReverseDialNumber('');
                      setError('');
                    }}
                    className="flex items-center gap-1 mb-5 text-[12px] tracking-[0.12em] uppercase font-semibold"
                    style={labelMuted}
                  >
                    <ChevronLeft size={16} /> Изменить номер
                  </button>
                  <h2
                    className="text-xl font-bold mb-2"
                    style={{ fontFamily: 'Unbounded, sans-serif', letterSpacing: '-0.02em' }}
                  >
                    Позвоните на номер
                  </h2>
                  <p className="text-sm mb-5 leading-relaxed" style={labelMuted}>
                    После звонка вход выполнится автоматически.
                  </p>
                  <p
                    className="text-center text-2xl font-bold mb-5"
                    style={{ letterSpacing: '0.04em', fontFamily: 'Manrope, sans-serif' }}
                  >
                    {formatDialDisplay(reverseDialNumber)}
                  </p>
                  {error && <p className="text-red-400 text-xs mb-3">{error}</p>}
                  <a
                    href={`tel:+${reverseDialNumber.replace(/\D/g, '')}`}
                    className="w-full flex items-center justify-center gap-2 py-4 rounded-2xl font-semibold text-sm tracking-[0.1em] uppercase transition-all active:scale-[0.99]"
                    style={primaryBtn}
                  >
                    <Phone size={17} /> Позвонить
                  </a>
                  <div className="flex flex-col items-center gap-2 mt-7">
                    <Loader2 size={20} className="animate-spin" style={{ color: 'rgba(255,255,255,0.4)' }} />
                    <p className="text-[11px] tracking-[0.12em] uppercase" style={labelMuted}>
                      Ожидаем подтверждение
                    </p>
                  </div>
                </div>
              </motion.div>
            ) : step === 'phone' ? (
              <motion.div
                key="phone"
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -16 }}
                transition={{ duration: 0.35 }}
              >
                <div className="rounded-3xl p-6" style={cardStyle}>
                  <button
                    type="button"
                    onClick={() => {
                      if (window.history.length > 1) navigate(-1);
                      else navigate('/app/home');
                    }}
                    className="flex items-center gap-1 mb-5 text-[12px] tracking-[0.12em] uppercase font-semibold"
                    style={labelMuted}
                  >
                    <ChevronLeft size={16} /> Назад
                  </button>
                  <h2
                    className="text-xl font-bold mb-2"
                    style={{ fontFamily: 'Unbounded, sans-serif', letterSpacing: '-0.02em' }}
                  >
                    Вход
                  </h2>
                  <p className="text-sm mb-6 leading-relaxed" style={labelMuted}>
                    {afterLoginPath.includes('/events')
                      ? 'После входа сможете записаться на событие'
                      : 'Введите номер телефона, чтобы продолжить'}
                  </p>

                  <div
                    className="flex items-center gap-3 rounded-2xl px-4 py-4 mb-2"
                    style={inputStyle(!!error)}
                  >
                    <Phone size={17} style={{ color: 'rgba(255,255,255,0.35)' }} className="shrink-0" />
                    <input
                      type="tel"
                      placeholder="+7 (___) ___-__-__"
                      value={phone}
                      onChange={handlePhoneChange}
                      onKeyDown={(e) => {
                        handlePhoneKeyDown(e);
                        if (e.key === 'Enter') handleSendCode();
                      }}
                      disabled={loading}
                      className="flex-1 bg-transparent text-white text-base font-medium outline-none"
                      style={{ letterSpacing: '0.03em', caretColor: '#fff', fontFamily: 'Manrope, sans-serif' }}
                    />
                  </div>

                  {error && <p className="text-red-400 text-xs mb-3">{error}</p>}

                  {smsOfferVisible && (
                    <button
                      type="button"
                      onClick={handleSendCodeViaSms}
                      disabled={loading}
                      className="w-full mb-3 flex items-center justify-center gap-2 py-4 rounded-2xl font-semibold text-sm tracking-[0.08em] uppercase transition-all active:scale-[0.99] disabled:opacity-60"
                      style={{
                        background: 'transparent',
                        color: '#fff',
                        border: '1px solid rgba(255,255,255,0.22)',
                        fontFamily: 'Manrope, sans-serif',
                      }}
                    >
                      {loading ? <Loader2 size={18} className="animate-spin" /> : 'Код по SMS'}
                    </button>
                  )}

                  <button
                    onClick={handleSendCode}
                    disabled={loading}
                    className="w-full mt-3 flex items-center justify-center gap-2 py-4 rounded-2xl font-semibold text-sm tracking-[0.1em] uppercase transition-all active:scale-[0.99] disabled:opacity-60"
                    style={primaryBtn}
                  >
                    {loading ? (
                      <Loader2 size={18} className="animate-spin" />
                    ) : (
                      <>
                        Получить код <ArrowRight size={17} />
                      </>
                    )}
                  </button>
                </div>

                <p
                  className="text-center text-[11px] mt-6 px-2 leading-relaxed"
                  style={{ color: 'rgba(255,255,255,0.28)', fontFamily: 'Manrope, sans-serif' }}
                >
                  Нажимая «Получить код», вы соглашаетесь с условиями использования сервиса
                </p>
              </motion.div>
            ) : step === 'intro' ? (
              <motion.div
                key="intro"
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -16 }}
                transition={{ duration: 0.35 }}
              >
                <div className="rounded-3xl p-6" style={cardStyle}>
                  <p
                    className="text-[11px] tracking-[0.22em] uppercase mb-3"
                    style={labelMuted}
                  >
                    Профиль
                  </p>
                  <h2
                    className="text-xl font-bold mb-2"
                    style={{ fontFamily: 'Unbounded, sans-serif', letterSpacing: '-0.02em' }}
                  >
                    Познакомимся
                  </h2>
                  <p className="text-sm mb-6 leading-relaxed" style={labelMuted}>
                    Имя и фамилию можно указать сейчас или позже в профиле
                  </p>

                  <label className="block text-[11px] tracking-[0.16em] uppercase mb-2" style={labelMuted}>
                    Имя
                  </label>
                  <input
                    type="text"
                    placeholder="Как к вам обращаться"
                    value={firstNameInput}
                    onChange={(e) => {
                      setFirstNameInput(e.target.value);
                      setError('');
                    }}
                    disabled={loading}
                    className="w-full rounded-2xl px-4 py-4 mb-4 text-white text-base font-medium outline-none"
                    style={inputStyle()}
                  />
                  <label className="block text-[11px] tracking-[0.16em] uppercase mb-2" style={labelMuted}>
                    Фамилия
                  </label>
                  <input
                    type="text"
                    placeholder="Необязательно"
                    value={lastNameInput}
                    onChange={(e) => {
                      setLastNameInput(e.target.value);
                      setError('');
                    }}
                    disabled={loading}
                    className="w-full rounded-2xl px-4 py-4 mb-2 text-white text-base font-medium outline-none"
                    style={inputStyle()}
                  />

                  {error && <p className="text-red-400 text-xs mb-3">{error}</p>}

                  <button
                    onClick={handleIntroContinue}
                    disabled={loading}
                    className="w-full mt-4 flex items-center justify-center gap-2 py-4 rounded-2xl font-semibold text-sm tracking-[0.1em] uppercase transition-all active:scale-[0.99] disabled:opacity-60"
                    style={primaryBtn}
                  >
                    {loading ? (
                      <Loader2 size={18} className="animate-spin" />
                    ) : (
                      <>
                        Продолжить <ArrowRight size={17} />
                      </>
                    )}
                  </button>

                  <button
                    type="button"
                    onClick={handleIntroSkip}
                    disabled={loading}
                    className="w-full mt-3 py-3 text-[12px] tracking-[0.12em] uppercase font-semibold transition-all active:scale-[0.99] disabled:opacity-40"
                    style={labelMuted}
                  >
                    Пропустить
                  </button>
                </div>
              </motion.div>
            ) : step === 'notifications' ? (
              <motion.div
                key="notifications"
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -16 }}
                transition={{ duration: 0.35 }}
              >
                <div className="rounded-3xl p-6" style={cardStyle}>
                  <p
                    className="text-[11px] tracking-[0.22em] uppercase mb-3"
                    style={labelMuted}
                  >
                    Уведомления
                  </p>
                  <h2
                    className="text-xl font-bold mb-3 leading-snug"
                    style={{ fontFamily: 'Unbounded, sans-serif', letterSpacing: '-0.02em' }}
                  >
                    Статусы заказа
                  </h2>
                  <p className="text-sm mb-7 leading-relaxed" style={labelMuted}>
                    Присылать push о готовности и доставке? Можно включить позже в профиле.
                  </p>
                  <button
                    type="button"
                    onClick={handleNotificationsYes}
                    disabled={loading}
                    className="w-full flex items-center justify-center gap-2 py-4 rounded-2xl font-semibold text-sm tracking-[0.1em] uppercase transition-all active:scale-[0.99] disabled:opacity-60"
                    style={primaryBtn}
                  >
                    {loading ? <Loader2 size={18} className="animate-spin" /> : 'Включить'}
                  </button>
                  <button
                    type="button"
                    onClick={handleNotificationsSkip}
                    disabled={loading}
                    className="w-full mt-3 py-3 text-[12px] tracking-[0.12em] uppercase font-semibold transition-all active:scale-[0.99] disabled:opacity-40"
                    style={labelMuted}
                  >
                    Пропустить
                  </button>
                </div>
              </motion.div>
            ) : (
              <motion.div
                key="code"
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -16 }}
                transition={{ duration: 0.35 }}
              >
                <div className="rounded-3xl p-6" style={cardStyle}>
                  <button
                    type="button"
                    onClick={() => {
                      setStep('phone');
                      setCode(['', '', '', '']);
                      setOtpChannel(null);
                      setReverseSessionId(null);
                      setReverseDialNumber('');
                      setError('');
                    }}
                    className="flex items-center gap-1 mb-5 text-[12px] tracking-[0.12em] uppercase font-semibold"
                    style={labelMuted}
                  >
                    <ChevronLeft size={16} /> Изменить номер
                  </button>

                  <h2
                    className="text-xl font-bold mb-2"
                    style={{ fontFamily: 'Unbounded, sans-serif', letterSpacing: '-0.02em' }}
                  >
                    {otpChannel === 'flash_call' ? 'Код из звонка' : 'Код из SMS'}
                  </h2>
                  <p className="text-sm mb-6 leading-relaxed" style={labelMuted}>
                    {otpChannel === 'flash_call'
                      ? 'Введите последние 4 цифры пропущенного входящего'
                      : `Отправили на ${phone}`}
                  </p>

                  <div className="flex gap-3 justify-center mb-2">
                    {code.map((digit, i) => (
                      <input
                        key={i}
                        ref={codeRefs[i]}
                        type="tel"
                        inputMode="numeric"
                        maxLength={1}
                        value={digit}
                        onChange={(e) => handleCodeChange(i, e.target.value)}
                        onKeyDown={(e) => handleCodeKeyDown(i, e)}
                        disabled={loading}
                        className="w-14 h-14 text-center text-white text-2xl font-bold rounded-2xl outline-none transition-all"
                        style={{
                          ...inputStyle(!!error),
                          border: error
                            ? '1px solid rgba(248,113,113,0.55)'
                            : digit
                              ? '1px solid rgba(255,255,255,0.35)'
                              : '1px solid rgba(255,255,255,0.12)',
                          fontFamily: 'Unbounded, sans-serif',
                        }}
                      />
                    ))}
                  </div>

                  {error && <p className="text-red-400 text-xs mb-3 text-center">{error}</p>}

                  <button
                    onClick={() => handleVerifyCode()}
                    disabled={loading || code.some((d) => !d)}
                    className="w-full mt-5 flex items-center justify-center gap-2 py-4 rounded-2xl font-semibold text-sm tracking-[0.1em] uppercase transition-all active:scale-[0.99] disabled:opacity-60"
                    style={primaryBtn}
                  >
                    {loading ? (
                      <Loader2 size={18} className="animate-spin" />
                    ) : (
                      <>
                        Войти <ArrowRight size={17} />
                      </>
                    )}
                  </button>

                  <button
                    onClick={handleSendCode}
                    disabled={loading}
                    className="w-full mt-3 py-3 text-[12px] tracking-[0.12em] uppercase font-semibold transition-all active:scale-[0.99] disabled:opacity-40"
                    style={labelMuted}
                  >
                    Отправить повторно
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}
